import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { showError } from '../Alert';
import {
  fetchStoreDashboard,
  type StoreDashboardDTO,
} from '../../services/store';
import '../../styles/DashboardTab.css';

const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  }).format(typeof value === 'number' ? value : 0);

const formatDateForInput = (date: Date) => date.toISOString().slice(0, 10);

export default function DashboardTab() {
  const defaultDates = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return {
      startDate: formatDateForInput(start),
      endDate: formatDateForInput(end),
    };
  }, []);

  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [dashboard, setDashboard] = useState<StoreDashboardDTO | null>(null);
  const [loading, setLoading] = useState(false);

  const loadDashboard = () => {
    setLoading(true);
    fetchStoreDashboard({ startDate, endDate })
      .then((res) => setDashboard(res.data))
      .catch((err) => {
        console.error('[DashboardTab] Failed to load dashboard:', err);
        const message =
          typeof err?.response?.data === 'string'
            ? err.response.data
            : 'Failed to load dashboard.';
        showError(message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metrics = [
    {
      label: 'Total sold',
      value: formatCurrency(dashboard?.totalSold),
    },
    {
      label: 'Next payout estimate',
      value: formatCurrency(dashboard?.estimatedNextPayment),
    },
    {
      label: 'Pending collection',
      value: formatCurrency(dashboard?.totalPendingCollection),
    },
    {
      label: 'Collected',
      value: formatCurrency(dashboard?.totalCollected),
    },
    {
      label: 'Collectable after commission',
      value: formatCurrency(dashboard?.totalCollectable),
    },
    {
      label: 'Shipping to pay',
      value: formatCurrency(dashboard?.totalShippingToPay),
    },
  ];

  if (loading && !dashboard) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} className="dashboard-tab">
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        className="dashboard-filters"
      >
        <TextField
          label="Start date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          onClick={loadDashboard}
          disabled={loading}
          className="dashboard-refresh"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Stack>

      <div className="dashboard-metrics">
        {metrics.map((metric) => (
          <Paper key={metric.label} variant="outlined" className="metric-card">
            <Typography className="metric-label">{metric.label}</Typography>
            <Typography className="metric-value">{metric.value}</Typography>
          </Paper>
        ))}
      </div>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell align="center">Units sold</TableCell>
              <TableCell align="center">Total sold</TableCell>
              <TableCell align="center">Collectable</TableCell>
              <TableCell align="center">Collected</TableCell>
              <TableCell align="center">Pending</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(dashboard?.itemSales ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No sales in this date range.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dashboard!.itemSales.map((item) => (
                <TableRow key={item.itemId}>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell align="center">{item.unitsSold}</TableCell>
                  <TableCell align="center">
                    {formatCurrency(item.totalSold)}
                  </TableCell>
                  <TableCell align="center">
                    {formatCurrency(item.collectableAmount)}
                  </TableCell>
                  <TableCell align="center">
                    {formatCurrency(item.collectedAmount)}
                  </TableCell>
                  <TableCell align="center">
                    {formatCurrency(item.pendingAmount)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
